import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, TextField, Button, Popover,
} from '@mui/material';
import { formatAmount } from '../../utils/formatAmount';
import ReportService from './ReportService';
import ExpenseService from '../money/expense/ExpenseService';

const today = () => new Date().toISOString().slice(0, 10);

function AddPopover({ optionId, optionName, currentMonth, onSaved }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!amount) return;
    setSaving(true);
    try {
      await ExpenseService.saveExpenseRecord({ expenseDate: date, optionId, amount: Number(amount), note: note || undefined });
      setAnchorEl(null);
      setAmount('');
      setNote('');
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button size="small" variant="outlined" color="error" onClick={e => setAnchorEl(e.currentTarget)}>
        + Add
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, width: 260 }}>
          <Typography variant="subtitle2" fontWeight={700}>{optionName}</Typography>
          <TextField label="Amount (THB)" type="number" size="small" value={amount} onChange={e => setAmount(e.target.value)} autoFocus />
          <TextField label="Date" type="date" size="small" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Note (optional)" size="small" value={note} onChange={e => setNote(e.target.value)} />
          <Button variant="contained" onClick={handleSave} disabled={saving || !amount}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Popover>
    </>
  );
}

export default function ExpenseReminderPanel() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await ReportService.getExpenseComparison();
    setData(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const { months, options } = data;
  const currentMonth = months[months.length - 1];
  const prevMonths = months.slice(0, months.length - 1);

  const missingCount = options.filter(o => {
    const hadBefore = prevMonths.some(m => o.amounts[m] != null);
    return hadBefore && o.amounts[currentMonth] == null;
  }).length;

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Typography variant="h6" fontWeight={700}>Expense Reminder</Typography>
        {missingCount > 0 && (
          <Chip label={`${missingCount} possibly missing this month`} color="error" size="small" />
        )}
      </div>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#37474f' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>Category</TableCell>
              {prevMonths.map(m => (
                <TableCell key={m} sx={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>{m}</TableCell>
              ))}
              <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>{currentMonth} (this month)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {options.map(o => {
              const hadBefore = prevMonths.some(m => o.amounts[m] != null);
              const hasCurrent = o.amounts[currentMonth] != null;
              const isMissing = hadBefore && !hasCurrent;

              return (
                <TableRow key={o.id} hover sx={{ backgroundColor: isMissing ? '#fff8f8' : 'inherit' }}>
                  <TableCell sx={{ fontSize: 12, fontWeight: isMissing ? 700 : 400 }}>{o.name}</TableCell>
                  {prevMonths.map(m => (
                    <TableCell key={m} sx={{ fontSize: 12, color: o.amounts[m] != null ? '#333' : '#ccc' }}>
                      {o.amounts[m] != null ? formatAmount(o.amounts[m]) : '—'}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontSize: 12 }}>
                    {hasCurrent ? (
                      <span style={{ color: '#2e7d32', fontWeight: 600 }}>{formatAmount(o.amounts[currentMonth])}</span>
                    ) : (
                      <AddPopover optionId={o.id} optionName={o.name} currentMonth={currentMonth} onSaved={load} />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
